/**
 * LabelSizeSection - Configure default label size for printing
 *
 * Two zones:
 * Zone A: Organization Default - sets the default for all locations
 * Zone B: Location Overrides - per-location label size overrides (BLU-313)
 *
 * Built-in presets (from CANVAS_PRESETS):
 * - Compact (32x57mm) - Small labels for limited space
 * - Standard (4x3") - Most common, good balance of readability and size
 * - Shipping (4x6") - Large labels for detailed information
 *
 * Custom sizes:
 * - User-defined dimensions (20-200mm width/height)
 * - Stored as JSONB array in prod_settings (org-wide)
 * - Can be deleted without affecting existing templates
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Loader2,
  AlertTriangle,
  RefreshCw,
  Save,
  Plus,
  Trash2,
  X,
  Building2,
  MapPin,
  RotateCcw,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { CANVAS_PRESETS, type CanvasPresetKey, type CanvasSize } from './label-layout/label-layout-types';
import { cn } from '@/lib/utils';

/** Helper to find preset key from canvas size */
function getPresetKeyFromSize(size: CanvasSize): CanvasPresetKey | null {
  for (const [key, preset] of Object.entries(CANVAS_PRESETS)) {
    if (preset.width === size.width && preset.height === size.height) {
      return key as CanvasPresetKey;
    }
  }
  return null;
}

/** Size preview component showing relative label dimensions */
function SizePreview({ size, isSelected }: { size: CanvasSize; isSelected: boolean }) {
  // Scale factor to show relative sizes (max height ~40px)
  const maxHeight = 40;
  const scale = maxHeight / Math.max(...Object.values(CANVAS_PRESETS).map(p => p.height));
  const height = size.height * scale;
  const width = size.width * scale;

  return (
    <div
      className={cn(
        'rounded border-2 transition-colors',
        isSelected ? 'border-primary bg-primary/10' : 'border-muted bg-muted/30'
      )}
      style={{ width: `${width}px`, height: `${height}px` }}
      aria-hidden="true"
    />
  );
}

/** Build a flat list of all available sizes (presets + custom) for Select dropdowns */
function getAllSizeOptions(customSizes: Array<{ width: number; height: number; name: string }>) {
  const options: Array<{ value: string; label: string; size: CanvasSize }> = [];
  for (const [key, size] of Object.entries(CANVAS_PRESETS)) {
    options.push({ value: key, label: `${size.name} (${size.width}x${size.height}mm)`, size });
  }
  for (const size of customSizes) {
    options.push({ value: `custom:${size.name}`, label: `${size.name} (${size.width}x${size.height}mm)`, size });
  }
  return options;
}

/** Get display name for a canvas size */
function getSizeName(size: { width: number; height: number; name: string }) {
  return `${size.name} (${size.width}x${size.height}mm)`;
}

// ============================================================================
// LOCATION LABEL OVERRIDES (Zone B)
// ============================================================================

interface LocationOverrideRowProps {
  location: {
    id: string;
    name: string;
    defaultLabelSize: { width: number; height: number; name: string } | null;
  };
  orgDefaultName: string;
  sizeOptions: Array<{ value: string; label: string; size: CanvasSize }>;
  onSetOverride: (locationId: string, size: CanvasSize) => void;
  onClearOverride: (locationId: string) => void;
  isSaving: boolean;
}

function LocationOverrideRow({
  location,
  orgDefaultName,
  sizeOptions,
  onSetOverride,
  onClearOverride,
  isSaving,
}: LocationOverrideRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const hasOverride = location.defaultLabelSize != null;

  const handleSelectSize = (value: string) => {
    const option = sizeOptions.find(o => o.value === value);
    if (option) {
      onSetOverride(location.id, option.size);
      setIsEditing(false);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors" role="listitem">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-medium text-sm truncate">{location.name}</span>
        {hasOverride ? (
          <Badge variant="info" className="shrink-0">
            Custom
          </Badge>
        ) : (
          <Badge variant="outline" className="text-muted-foreground border-muted-foreground/30 font-normal shrink-0">
            Using default
          </Badge>
        )}
      </div>

      <div className="flex items-center gap-2 sm:ml-auto">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Select onValueChange={handleSelectSize}>
              <SelectTrigger className="w-full sm:w-[220px] h-10" aria-label={`Label size for ${location.name}`}>
                <SelectValue placeholder="Select size..." />
              </SelectTrigger>
              <SelectContent>
                {sizeOptions.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 shrink-0"
              onClick={() => setIsEditing(false)}
              aria-label={`Cancel override for ${location.name}`}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <>
            <span className={cn(
              'text-sm',
              hasOverride ? 'text-foreground font-medium' : 'text-muted-foreground italic'
            )}>
              {hasOverride ? getSizeName(location.defaultLabelSize!) : orgDefaultName}
            </span>
            {hasOverride ? (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-10 text-xs"
                  onClick={() => setIsEditing(true)}
                  disabled={isSaving}
                  aria-label={`Change label size for ${location.name}`}
                >
                  Change
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 text-muted-foreground hover:text-foreground"
                  onClick={() => onClearOverride(location.id)}
                  disabled={isSaving}
                  aria-label={`Reset ${location.name} to organization default`}
                  title="Reset to organization default"
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </div>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                className="h-10 text-xs"
                onClick={() => setIsEditing(true)}
                disabled={isSaving}
                aria-label={`Override label size for ${location.name}`}
              >
                Override
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function LocationLabelOverrides({
  orgDefault,
  customSizes,
}: {
  orgDefault: CanvasSize;
  customSizes: Array<{ width: number; height: number; name: string }>;
}) {
  const locationsQuery = trpc.productionSettings.getLocationSettings.useQuery();
  const utils = trpc.useUtils();

  const updateMutation = trpc.productionSettings.updateLocationLabelSizeSettings.useMutation({
    onSuccess: (_data, variables) => {
      const isClearing = variables.defaultLabelSize === null;
      const location = locationsQuery.data?.find(l => l.id === variables.locationId);
      const locName = location?.name ?? 'Location';
      toast.success(
        isClearing
          ? `Label size for ${locName} reset to organization default`
          : `Label size for ${locName} updated`
      );
      utils.productionSettings.getLocationSettings.invalidate();
      utils.productionSettings.getLocationLabelSizeSettings.invalidate();
    },
    onError: (error) => {
      toast.error(`Failed to update: ${error.message}`);
    },
  });

  const locations = useMemo(() =>
    (locationsQuery.data ?? []).filter(l => l.productionEnabled),
    [locationsQuery.data]
  );

  const sizeOptions = useMemo(() => getAllSizeOptions(customSizes), [customSizes]);
  const orgDefaultName = getSizeName(orgDefault);

  const handleSetOverride = (locationId: string, size: CanvasSize) => {
    updateMutation.mutate({
      locationId,
      defaultLabelSize: { width: size.width, height: size.height, name: size.name },
    });
  };

  const handleClearOverride = (locationId: string) => {
    updateMutation.mutate({ locationId, defaultLabelSize: null });
  };

  if (locationsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center py-6" role="status" aria-busy="true">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="ml-2 text-sm text-muted-foreground">Loading locations...</span>
      </div>
    );
  }

  if (locations.length === 0) {
    return (
      <p className="text-sm text-muted-foreground py-4">
        No locations have production enabled. Enable production for a location in General &gt; Location Settings to configure label defaults.
      </p>
    );
  }

  const hasAnyOverride = locations.some(l => l.defaultLabelSize != null);

  return (
    <div className="space-y-2">
      {!hasAnyOverride && (
        <p className="text-sm text-muted-foreground pb-1">
          All locations are using the organization default. Click &quot;Override&quot; to set a different size for a specific location.
        </p>
      )}
      <div className="space-y-1" role="list" aria-label="Location label size overrides">
        {locations.map(location => (
          <LocationOverrideRow
            key={location.id}
            location={location}
            orgDefaultName={orgDefaultName}
            sizeOptions={sizeOptions}
            onSetOverride={handleSetOverride}
            onClearOverride={handleClearOverride}
            isSaving={updateMutation.isPending}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function LabelSizeSection() {
  const { t } = useTranslation('settings');
  // Selected radio value: either a preset key or `custom:<name>`
  const [selectedValue, setSelectedValue] = useState<string>('standard-4x3');
  const [hasChanges, setHasChanges] = useState(false);

  // Custom size creation form
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customName, setCustomName] = useState('');
  const [customWidth, setCustomWidth] = useState('');
  const [customHeight, setCustomHeight] = useState('');

  // Delete confirmation
  const [pendingDeleteSize, setPendingDeleteSize] = useState<string | null>(null);

  // Queries
  const settingsQuery = trpc.productionSettings.getLabelSizeSettings.useQuery();
  const customSizesQuery = trpc.productionSettings.getCustomLabelSizes.useQuery();

  // Mutations
  const updateMutation = trpc.productionSettings.updateLabelSizeSettings.useMutation({
    onSuccess: () => {
      toast.success(t('production.labelSize.saved'));
      setHasChanges(false);
      settingsQuery.refetch();
    },
    onError: (error) => {
      toast.error(`Failed to save: ${error.message}`);
    },
  });

  const createCustomMutation = trpc.productionSettings.createCustomLabelSize.useMutation({
    onSuccess: () => {
      toast.success(t('production.labelSize.customCreated'));
      setShowCustomForm(false);
      setCustomName('');
      setCustomWidth('');
      setCustomHeight('');
      customSizesQuery.refetch();
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const deleteCustomMutation = trpc.productionSettings.deleteCustomLabelSize.useMutation({
    onSuccess: () => {
      toast.success(t('production.labelSize.customDeleted'));
      customSizesQuery.refetch();
      // If deleted size was selected, reset to default
      if (pendingDeleteSize && selectedValue === `custom:${pendingDeleteSize}`) {
        setSelectedValue('standard-4x3');
        setHasChanges(true);
      }
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const customSizes = useMemo(() => customSizesQuery.data?.customSizes ?? [], [customSizesQuery.data]);

  // Sync local state with server data
  useEffect(() => {
    if (settingsQuery.data) {
      const size = settingsQuery.data.defaultLabelSize;
      const presetKey = getPresetKeyFromSize(size);
      if (presetKey) {
        setSelectedValue(presetKey);
      } else {
        // Check if it matches a custom size
        const customMatch = customSizes.find(
          c => c.width === size.width && c.height === size.height
        );
        if (customMatch) {
          setSelectedValue(`custom:${customMatch.name}`);
        }
      }
      setHasChanges(false);
    }
  }, [settingsQuery.data, customSizes]);

  const handleValueChange = (value: string) => {
    setSelectedValue(value);
    setHasChanges(true);
  };

  const getSelectedSize = (): CanvasSize | null => {
    // Check preset keys
    if (selectedValue in CANVAS_PRESETS) {
      return CANVAS_PRESETS[selectedValue as CanvasPresetKey];
    }
    // Check custom sizes
    if (selectedValue.startsWith('custom:')) {
      const name = selectedValue.slice(7);
      const custom = customSizes.find(c => c.name === name);
      if (custom) return custom;
    }
    return null;
  };

  const handleSave = () => {
    const size = getSelectedSize();
    if (!size) return;
    updateMutation.mutate({
      defaultLabelSize: {
        width: size.width,
        height: size.height,
        name: size.name,
      },
    });
  };

  const handleCreateCustomSize = () => {
    const width = parseInt(customWidth, 10);
    const height = parseInt(customHeight, 10);

    if (!customName.trim()) {
      toast.error('Name is required');
      return;
    }
    if (isNaN(width) || width < 20 || width > 200) {
      toast.error('Width must be between 20 and 200mm');
      return;
    }
    if (isNaN(height) || height < 20 || height > 200) {
      toast.error('Height must be between 20 and 200mm');
      return;
    }

    createCustomMutation.mutate({ name: customName.trim(), width, height });
  };

  const handleDeleteCustomSize = (name: string) => {
    deleteCustomMutation.mutate({ name });
    setPendingDeleteSize(null);
  };

  const isLoading = settingsQuery.isLoading;
  const hasError = settingsQuery.isError;

  const content = (
    <div className="space-y-6">
        {/* Zone A: Organization Default */}
        <div className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
          <div>
            <h4 className="font-medium text-base">Organization Default</h4>
            <p className="text-xs text-muted-foreground">All locations use this size unless overridden below</p>
          </div>
        </div>

        {/* Error State */}
        {hasError && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Failed to load label size settings</AlertTitle>
            <AlertDescription className="flex items-center gap-2">
              {settingsQuery.error?.message || 'Unknown error'}
              <Button
                variant="link"
                className="p-0 h-auto"
                onClick={() => settingsQuery.refetch()}
              >
                <RefreshCw className="h-3 w-3 mr-1" />
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">Loading label size settings...</span>
          </div>
        )}

        {!isLoading && !hasError && (
          <RadioGroup
            value={selectedValue}
            onValueChange={handleValueChange}
            className="space-y-3"
          >
            {/* Built-in presets */}
            {Object.entries(CANVAS_PRESETS).map(([key, size]) => (
              <div
                key={key}
                className="flex items-center space-x-4 p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
              >
                <RadioGroupItem value={key} id={`label-size-${key}`} className="mt-0" />
                <div className="flex-1 min-w-0">
                  <Label htmlFor={`label-size-${key}`} className="font-medium cursor-pointer text-base">
                    {size.name}
                  </Label>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {size.width}mm x {size.height}mm
                  </p>
                </div>
                <SizePreview size={size} isSelected={selectedValue === key} />
              </div>
            ))}

            {/* Custom sizes */}
            {customSizes.map((size) => {
              const radioValue = `custom:${size.name}`;
              return (
                <div
                  key={radioValue}
                  className="flex items-center space-x-4 p-4 rounded-lg border bg-muted/30 hover:bg-muted/50 transition-colors"
                >
                  <RadioGroupItem value={radioValue} id={`label-size-${radioValue}`} className="mt-0" />
                  <div className="flex-1 min-w-0">
                    <Label htmlFor={`label-size-${radioValue}`} className="font-medium cursor-pointer text-base">
                      {size.name}
                    </Label>
                    <p className="text-sm text-muted-foreground mt-0.5">
                      {size.width}mm x {size.height}mm &middot; Custom
                    </p>
                  </div>
                  <SizePreview size={size} isSelected={selectedValue === radioValue} />
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 hover:bg-destructive/10 shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      setPendingDeleteSize(size.name);
                    }}
                    aria-label={`Delete "${size.name}"`}
                    tabIndex={-1}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              );
            })}
          </RadioGroup>
        )}

        {/* Add Custom Size */}
        {!isLoading && !hasError && (
          <>
            {!showCustomForm ? (
              <Button
                variant="outline"
                size="sm"
                className="gap-2"
                onClick={() => setShowCustomForm(true)}
              >
                <Plus className="h-4 w-4" />
                Add Custom Size
              </Button>
            ) : (
              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">New Custom Size</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    onClick={() => {
                      setShowCustomForm(false);
                      setCustomName('');
                      setCustomWidth('');
                      setCustomHeight('');
                    }}
                    aria-label="Close custom size form"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="custom-name" className="text-xs">Name</Label>
                    <Input
                      id="custom-name"
                      value={customName}
                      onChange={(e) => setCustomName(e.target.value)}
                      placeholder='e.g., Barcode (2x1")'
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="custom-width" className="text-xs">Width (mm)</Label>
                    <Input
                      id="custom-width"
                      type="number"
                      min={20}
                      max={200}
                      value={customWidth}
                      onChange={(e) => setCustomWidth(e.target.value)}
                      placeholder="20-200"
                      className="h-9"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="custom-height" className="text-xs">Height (mm)</Label>
                    <Input
                      id="custom-height"
                      type="number"
                      min={20}
                      max={200}
                      value={customHeight}
                      onChange={(e) => setCustomHeight(e.target.value)}
                      placeholder="20-200"
                      className="h-9"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleCreateCustomSize}
                    disabled={createCustomMutation.isPending}
                    className="gap-1.5"
                  >
                    {createCustomMutation.isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <Plus className="h-3.5 w-3.5" />
                    )}
                    Add Size
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Info text */}
        {!isLoading && !hasError && (
          <p className="text-sm text-muted-foreground">
            Note: You can create and customize label templates for each size in the Label Layout Editor below.
            This setting determines which size template is loaded by default when printing.
          </p>
        )}

        {/* Zone B: Location Overrides */}
        {!isLoading && !hasError && (
          <>
            <Separator />
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <div>
                  <h4 className="font-medium text-base">Location Overrides</h4>
                  <p className="text-xs text-muted-foreground">Override the default label size for specific locations</p>
                </div>
              </div>
              <LocationLabelOverrides
                orgDefault={settingsQuery.data?.defaultLabelSize ?? { width: 102, height: 76, name: 'Standard (4x3")' }}
                customSizes={customSizes}
              />
            </div>
          </>
        )}

      {/* ARIA live region */}
      <div role="status" aria-live="polite" className="sr-only">
        {updateMutation.isSuccess && 'Label size settings saved successfully'}
        {hasChanges && 'You have unsaved changes'}
      </div>

      {/* Save button at the end for embedded mode */}
      {hasChanges && (
        <div className="flex justify-end pt-2">
          <Button
            onClick={handleSave}
            disabled={updateMutation.isPending}
            className="h-12 gap-2"
          >
            {updateMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>
      )}

      {/* Delete custom size confirmation */}
      <AlertDialog
        open={!!pendingDeleteSize}
        onOpenChange={(open) => { if (!open) setPendingDeleteSize(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Custom Size</AlertDialogTitle>
            <AlertDialogDescription>
              Delete &quot;{pendingDeleteSize}&quot;? Existing templates using this size will continue to work,
              but you won&apos;t be able to select this size when creating new templates.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (pendingDeleteSize) handleDeleteCustomSize(pendingDeleteSize);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );

  return content;
}
